import React from "react";

import BaseClass from "../../util/BaseClass";
import Context from "../../context/Context";
import Issues from "../components/common/Issues";
import {ModalController} from "../../common/components/Modal";
import OfficePlan from "./OfficePlan";
import PlanOpener from "./PlanOpener";
import Project from "./Project";
import Topic from "../../context/Topic";
import * as sourceUtil from "./sourceUtil";
import * as val from "../../util/val";

export default class Planner extends BaseClass {

  project;

  static getParentAppId(planItem) {
    let appId = null;
    if (planItem.versionInfo) {
      const appItem = Context.instance.configuration.appItem;
      return appItem && appItem.id;
    } else {
      const pfx = OfficePlan.TypeKeywordParentPfx;
      const a = planItem && planItem.typeKeywords;
      if (Array.isArray(a)) {
        a.some(v => {
          if (v && v.indexOf(pfx) === 0) {
            appId = v.substring(pfx.length);
          }
          return (!!appId);
        });
      }
    }
    return appId;
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */

  _handleOpenPlanError(task,project,error) {
    this._showOpenPlan(task,project);
    const errorValues = Object.values(error);
    let isPlanInvalid;
    let message = error.message;
    let submessage = error.submessage;
    if(errorValues && errorValues.length >0) 
    {
      if(errorValues[0] === "__invalidPlanId__") isPlanInvalid = true;
    }
    if(error.name === "identity-manager:not-authorized" || isPlanInvalid){
      const username=Context.instance.user.getUsername();
      submessage=null;
      message=Context.instance.i18n.spaceplanner.issues.noAccessToPlan;
      message=message.replace("{username}", username);
    }
    ModalController.confirm({
      isError: true,
      title: Context.instance.i18n.spaceplanner.issues.unableToOpenPlanTitle,
      message: message,
      submessage: submessage,
      hideCancel:true
    });
  }

  _handleOpenProjectError(task,project,error) {
    let message = error.message;
    let submessage = error.submessage;
    ModalController.confirm({
      isError: true,
      title: Context.instance.i18n.spaceplanner.issues.unableToOpenProjectTitle,
      message: message,
      submessage: submessage
    }).then(() => {
      this._showOpenPlan(task,project);
    });
  }

  hasValidPlan() {
    const plan = Context.instance.spaceplanner.activePlan;
    return !!(plan && plan.wasOpened && !plan.hasIssues());
  }

  hasValidProject() {
    return !!(this.project && !this.project.hasIssues());
  }

  isBaselinePeopleLayerValid() {
    const source = sourceUtil.getInitialPeopleSource();
    return !!(source && source.layer2D);
  }

  isBaselineUnitsLayerValid() {
    const source = sourceUtil.getInitialUnitsSource();
    return !!(source && source.layer2D);
  }

  load() {
    const promise = new Promise((resolve,reject) => {
      const lib = Context.instance.lib;
      let planId = Context.instance.spaceplanner.planId
      if (!planId) {
        const url = lib.esri.urlUtils.urlToObject(window.location.href);
        if (url && url.query) {
          planId = val.trim(url.query.planId || url.query.planid);
        }
      }

      const cfgOn = (Context.instance.uiMode.isConfiguratorOn ||
                     Context.instance.session.startInConfiguratorMode);
      const task = {
        cfgOn: cfgOn
      };

      const project = new Project();
      this.project = project;
      project.interrogateMap().then(() => {
        console.log("project",project)
        if (!project.hasIssues()) {
          if (planId) {
            return this._openPlan(task,project,planId);
          } else {
            return this._showOpenPlan(task,project);
          }
        } else {
          this._showProjectIssues(task,project);
        }
      }).then(() => {
        resolve();
      }).catch(ex =>  {
        console.warn("Error opening project",ex);
        this._handleOpenProjectError(task,project,ex);
        resolve();
      });

    });
    return promise;
  }

  _openPlan(task,project,planId) {
    const promise = new Promise((resolve,reject) => {
      if (Context.instance.spaceplanner.activePlan) {
        Context.instance.spaceplanner.activePlan.destroy();
      }
      const plan = new OfficePlan();
      const opener = new PlanOpener();
      Promise.resolve().then(result => {
        if (project.isVersioned) {
          return opener.openVersionedPlan(project,plan,planId);
        } else {
          return opener.openHostedPlan(project,plan,planId);
        }
      }).then(() => {
        return plan._init();
      }).then(() => {
        if (!plan.hasIssues()) {
          plan.wasOpened = true;
          Context.instance.spaceplanner.planId = planId;
          Context.instance.spaceplanner.activePlan = plan;
          Topic.publish(Topic.PlanOpened,{
            plan: plan,
            view: Context.instance.views.mapView
          });
          Topic.publish(Topic.CloseBackstage,{});
          console.log("planOpened",plan)
        } else {
          this._showPlanIssues(task,project,plan);
        }
      }).then(() => {
        resolve();
      }).catch(ex => {
        console.error("Error opening plan",ex); // TODO
        this._handleOpenPlanError(task,project,ex);
        resolve();
      });
    });
    return promise;
  };

  reloadApp(appId,planId) {
    const lib = Context.instance.lib;
    const urlObj = lib.esri.urlUtils.urlToObject(window.location.href);
    let href = urlObj.path;
    if (href.indexOf("#") > 0) href = href.substring(0,href.indexOf("#"));
    href += "?appid=" + encodeURIComponent(appId);
    href += "&planid=" + encodeURIComponent(planId);
    if (urlObj.query && urlObj.query.edit === "true") {
      href += "&edit=true";
    }
    if (urlObj.query && urlObj.query.locale) {
      href += "&locale=" + encodeURIComponent(urlObj.query.locale);
    }
    window.location.href = href;
  }

  _showOpenPlan(task,project) {
    const promise = new Promise((resolve,reject) => {
      if (Context.instance.spaceplanner.activePlan) {
        Context.instance.spaceplanner.activePlan.destroy();
      }
      const plan = new OfficePlan();
      Context.instance.spaceplanner.activePlan = plan;
      plan._init().then(() => {
        if (!task.cfgOn) {
          Topic.publish(Topic.ShowBackstage,{
            forceRefresh: true,
            tabKey: "openPlan"
          });
        }
        resolve();
      }).catch(ex => {
        reject(ex);
      });
    });
    return promise;
  }

  _showPlanIssues(task,project,plan) {
    const i18n = Context.instance.i18n;
    const title = i18n.spaceplanner.issues.unableToOpenPlanTitle;
    ModalController.confirm({
      title: title,
      content: <Issues issues={plan.issues} />
    }).then(result => {
      this._showOpenPlan(task,project);
    });
  }

  _showProjectIssues(task,project) {
    const i18n = Context.instance.i18n;
    //const title = i18n.spaceplanner.issues.projectIssues;
    const title = i18n.spaceplanner.issues.requiredLayersMissing;
    ModalController.confirm({
      title: title,
      content: <Issues issues={project.issues} />
    }).then(result => {
      this._showOpenPlan(task,project);
    });
  }

}
